import * as microsoftTeams from '@microsoft/teams-js'
import { PublicClientApplication, LogLevel } from '@azure/msal-browser'
import axios from 'axios'

export interface TeamsAuthConfig {
  clientId: string
  tenantId: string
  redirectUri: string
}

export interface TeamsUser {
  id: string
  displayName: string
  email: string
  upn: string
}

export interface AuthResult {
  success: boolean
  user?: TeamsUser
  token?: string
  error?: string
}

class TeamsAuthService {
  private msalInstance: PublicClientApplication | null = null
  private config: TeamsAuthConfig | null = null
  private isInitialized = false

  async initialize(config: TeamsAuthConfig): Promise<void> {
    this.config = config

    console.log('Initializing Teams auth with config:', {
      clientId: config.clientId ? '***' : 'MISSING',
      tenantId: config.tenantId,
      redirectUri: config.redirectUri,
      currentOrigin: window.location.origin,
    })

    // Validate required config
    if (!config.clientId) {
      throw new Error('Azure Client ID is required for Teams authentication')
    }

    // Initialize MSAL for Teams authentication
    try {
      this.msalInstance = new PublicClientApplication({
        auth: {
          clientId: config.clientId,
          authority: `https://login.microsoftonline.com/${config.tenantId}`,
          redirectUri: config.redirectUri,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false,
        },
        system: {
          loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
              if (containsPii) return
              console.log(`[MSAL] ${message}`)
            },
            piiLoggingEnabled: false,
            logLevel: LogLevel.Info,
          },
        },
      })

      await this.msalInstance.initialize()
      console.log('MSAL initialized as fallback for Teams authentication')
    } catch (msalError) {
      console.warn('MSAL initialization failed, will rely on Teams SDK only:', msalError)
    }

    this.isInitialized = true
    console.log('Teams auth service initialized successfully')
  }

  async isRunningInTeams(): Promise<boolean> {
    try {
      await microsoftTeams.app.initialize()
      return true
    } catch (error) {
      // This is expected when not running in Teams context
      console.log('Not running in Teams context - this is normal for local testing')
      return false
    }
  }

  async getTeamsContext(): Promise<unknown> {
    try {
      await microsoftTeams.app.initialize()
      return new Promise((resolve, reject) => {
        microsoftTeams.app
          .getContext()
          .then((context: unknown) => {
            resolve(context)
          })
          .catch((error: unknown) => {
            reject(new Error('Failed to get Teams context: ' + error))
          })
      })
    } catch (error) {
      throw new Error('Failed to get Teams context: ' + error)
    }
  }

  async authenticateWithTeams(): Promise<AuthResult> {
    if (!this.isInitialized) {
      throw new Error('TeamsAuthService not initialized')
    }

    try {
      // Check if running in Teams
      const inTeams = await this.isRunningInTeams()
      if (!inTeams) {
        return {
          success: false,
          error: 'Not running in Teams context',
        }
      }

      console.log('Starting Teams authentication...')

      // Get Teams context for user info
      const context = await this.getTeamsContext()
      console.log('Teams context:', context)

      // Extract user info from Teams context directly
      // This avoids both iframe origin and AADSTS500011 issues
      const teamsContext = context as {
        user?: {
          id?: string
          objectId?: string
          displayName?: string
          email?: string
          userPrincipalName?: string
        }
      }
      if (teamsContext.user) {
        const userInfo: TeamsUser = {
          id: teamsContext.user.id || teamsContext.user.objectId || '',
          displayName: teamsContext.user.displayName || '',
          email: teamsContext.user.email || teamsContext.user.userPrincipalName || '',
          upn: teamsContext.user.userPrincipalName || '',
        }

        console.log('Teams context authentication successful')
        return {
          success: true,
          user: userInfo,
          token: 'teams-context-token', // Dummy token since we're using context
        }
      }

      // Fallback: Try MSAL authentication
      if (this.msalInstance) {
        try {
          console.log('Attempting MSAL authentication as fallback...')
          const token = await this.getTeamsAccessToken()

          if (token) {
            console.log('MSAL authentication successful')
            const userInfo = await this.getUserInfo(token)
            return {
              success: true,
              user: userInfo,
              token: token,
            }
          }
        } catch (msalError) {
          console.log('MSAL authentication failed:', msalError)
        }
      }

      return {
        success: false,
        error: 'Failed to get user information from Teams context and MSAL fallback',
      }
    } catch (error) {
      console.error('Teams authentication error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  private async getTeamsAccessToken(): Promise<string | null> {
    try {
      // For Teams fallback, use MSAL with Microsoft Graph
      // This is only used if Teams context doesn't provide user info
      const scopes = ['User.Read', 'openid', 'profile', 'email']

      if (this.msalInstance) {
        // First try silent authentication
        try {
          const accounts = this.msalInstance.getAllAccounts()
          if (accounts.length > 0) {
            const silentRequest = {
              scopes,
              account: accounts[0],
            }
            const response = await this.msalInstance.acquireTokenSilent(silentRequest)
            return response.accessToken
          }
        } catch (silentError) {
          console.log('Silent authentication failed, trying interactive...', silentError)
        }

        // If silent fails, try interactive
        const interactiveRequest = {
          scopes,
          prompt: 'select_account' as const,
        }
        const response = await this.msalInstance.acquireTokenPopup(interactiveRequest)
        return response.accessToken
      }
      return null
    } catch (error) {
      console.error('Failed to get Teams access token:', error)
      return null
    }
  }

  private async getUserInfoFromToken(accessToken: string): Promise<TeamsUser> {
    try {
      // Try to decode the token to get user info
      const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]))
      return {
        id: tokenPayload.oid || tokenPayload.sub,
        displayName:
          tokenPayload.name ||
          `${tokenPayload.given_name || ''} ${tokenPayload.family_name || ''}`.trim(),
        email: tokenPayload.email || tokenPayload.preferred_username,
        upn: tokenPayload.preferred_username || tokenPayload.upn,
      }
    } catch (tokenError) {
      console.error('Failed to decode token:', tokenError)

      // Fallback: try to get user info from Teams context
      try {
        const context = await this.getTeamsContext()
        const teamsContext = context as {
          user?: {
            id?: string
            objectId?: string
            displayName?: string
            email?: string
            userPrincipalName?: string
          }
        }
        if (teamsContext.user) {
          return {
            id: teamsContext.user.id || teamsContext.user.objectId || '',
            displayName: teamsContext.user.displayName || '',
            email: teamsContext.user.email || teamsContext.user.userPrincipalName || '',
            upn: teamsContext.user.userPrincipalName || '',
          }
        }
      } catch (contextError) {
        console.error('Failed to get Teams context:', contextError)
      }

      throw new Error('Unable to extract user information from token or Teams context')
    }
  }

  private async getUserInfo(accessToken: string): Promise<TeamsUser> {
    try {
      const response = await axios.get('https://graph.microsoft.com/v1.0/me', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      return {
        id: response.data.id,
        displayName: response.data.displayName,
        email: response.data.mail || response.data.userPrincipalName,
        upn: response.data.userPrincipalName,
      }
    } catch (error) {
      console.error('Failed to get user info:', error)
      throw new Error('Failed to get user information')
    }
  }

  async validateTeamsToken(token: string): Promise<boolean> {
    try {
      const response = await axios.post('/api/validate-teams-token', {
        token,
      })
      return response.data.valid
    } catch (error) {
      console.error('Token validation failed:', error)
      return false
    }
  }

  async checkTeamsConfiguration(): Promise<{ isValid: boolean; errors: string[] }> {
    const errors: string[] = []

    // Check if running in Teams
    const inTeams = await this.isRunningInTeams()
    if (!inTeams) {
      errors.push('Not running in Teams context')
    }

    // Check configuration
    if (!this.config?.clientId) {
      errors.push('Azure Client ID is not configured')
    }

    if (!this.config?.tenantId) {
      errors.push('Azure Tenant ID is not configured')
    }

    if (!this.config?.redirectUri) {
      errors.push('Redirect URI is not configured')
    }

    // Check if Teams SDK is available
    if (typeof microsoftTeams === 'undefined') {
      errors.push('Microsoft Teams SDK is not loaded')
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  async logout(): Promise<void> {
    if (this.msalInstance) {
      const accounts = this.msalInstance.getAllAccounts()
      if (accounts.length > 0) {
        // Always use redirect for Teams context
        await this.msalInstance.logoutRedirect({
          account: accounts[0],
        })
      }
    }

    // Clear local storage
    localStorage.removeItem('teams_token')
    localStorage.removeItem('teams_user')
  }
}

export const teamsAuthService = new TeamsAuthService()
